import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Home as HomeIcon, Building2, Droplets, Layers, CloudRain, Clock, Users, ShieldCheck } from "lucide-react";
import SEO from "../../components/site/SEO";
import { api } from "../../lib/apiClient";

const iconMap = { home: HomeIcon, "building-2": Building2, droplets: Droplets, layers: Layers, "cloud-rain": CloudRain };

// Surface-specific safety copy per service slug (Phase 3 requirement — surface-safety prominently placed for house, roof, deck).
const SAFETY_COPY = {
  "house-washing": {
    label: "Safe for siding",
    body: "Our soft-wash system uses professional cleaning solutions and a gentle rinse at a fraction of rental-machine pressure. Won't strip paint, no water behind the walls, no etched surfaces — gentle on siding, tough on grime.",
  },
  "roof-exterior": {
    label: "Never blasted",
    body: "Wonder Washers never blasts a roof. Roof cleaning is soft-wash only — kills moss, algae, and lichen at the root with no shingle granule loss and no shortened roof life.",
  },
  "deck-cleaning": {
    label: "Safe for cedar",
    body: "Our fully adjustable system is dialed down to wood-safe pressure with wide-fan nozzles, cleaned with the grain. No fuzzing, no gouging, no wand marks — perfect prep for staining.",
  },
  "driveways-sidewalks": {
    label: "Hot water + surface cleaner",
    body: "Hot water up to 248°F combined with a professional flat-surface cleaner lifts grease, oil, and years of Oregon grime — with streak-free, no-zebra-stripe results.",
  },
  "commercial-buildings": {
    label: "Route-based reliability",
    body: "Onboard water tank, hot-water and soft-wash capability, and a DOT-compliant trailer mean we arrive ready — no dependence on your spigot, no downtime for your tenants or customers.",
  },
};

const serviceImg = (slug) => {
  switch (slug) {
    case "commercial-buildings":
      return "https://images.unsplash.com/photo-1523477593243-78bbf626fd3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwYnVpbGRpbmclMjBnbGFzcyUyMHdpbmRvd3N8ZW58MHx8fHwxNzg1OTU2NDM4fDA&ixlib=rb-4.1.0&q=85";
    case "driveways-sidewalks":
    case "roof-exterior":
      return "https://images.unsplash.com/photo-1707897283727-31befe824066?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxwcmVzc3VyZSUyMHdhc2hpbmclMjBob3VzZXxlbnwwfHx8fDE3ODU5NTY0NDN8MA&ixlib=rb-4.1.0&q=85";
    case "house-washing":
      return "https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yfGVufDB8fHx8MTc4NTk1NjQzM3ww&ixlib=rb-4.1.0&q=85";
    default:
      return "https://images.unsplash.com/photo-1740657254989-42fe9c3b8cce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxjbGVhbmluZyUyMHNlcnZpY2UlMjBjcmV3fGVufDB8fHx8MTc4NTk1NjQ0Mnww&ixlib=rb-4.1.0&q=85";
  }
};

export default function Services() {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then(r => setServices(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (window.location.hash) {
      const el = document.querySelector(window.location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [services]);

  return (
    <>
      <SEO title="Services — Wonder Washers Pressure Washing" description="House washing, driveways & sidewalks, deck cleaning, roof & exterior cleaning, and commercial pressure washing across Oregon. Safe for siding, roofs, and cedar." canonical="[BOOKING_URL]/services" />
      <section className="bg-[color:var(--ww-navy)] text-white py-20 md:py-24 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-gold)] uppercase mb-3">Services</div>
          <h1 className="font-display uppercase text-5xl sm:text-6xl leading-none">Five ways we make your property new again.</h1>
          <p className="mt-5 text-slate-300 max-w-2xl">Commercial-grade equipment, residential-grade care. Every job matched to the surface — soft-wash for siding and roofs, hot water and surface cleaner for flatwork, wood-safe pressure for cedar.</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24 space-y-12">
        {services.map((s, i) => {
          const Icon = iconMap[s.icon] || Droplets;
          const flip = i % 2 === 1;
          const safety = SAFETY_COPY[s.slug];
          return (
            <div id={s.slug} key={s.id} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 items-center scroll-mt-24 ${flip ? "lg:[&>div:first-child]:order-2" : ""}`}>
              <div className="lg:col-span-6">
                <span className="inline-flex items-center gap-2 ww-badge-blue"><Icon className="h-3.5 w-3.5" /> {s.name}</span>
                <h2 className="mt-4 font-display uppercase text-4xl text-[color:var(--ww-navy)] leading-tight">{s.name}</h2>
                <p className="mt-4 text-slate-600 leading-relaxed">{s.description}</p>
                {safety && (
                  <div className="mt-5 rounded-md border-l-4 border-[color:var(--ww-red)] bg-slate-50 p-4">
                    <div className="flex items-center gap-2 text-[color:var(--ww-red)] font-subhead uppercase tracking-widest text-xs">
                      <ShieldCheck className="h-4 w-4" /> {safety.label}
                    </div>
                    <p className="mt-2 text-sm text-slate-700 leading-relaxed">{safety.body}</p>
                  </div>
                )}
                <div className="mt-6 flex flex-wrap gap-3 text-sm">
                  <span className="ww-badge-slate"><Clock className="h-3.5 w-3.5" /> ~{s.duration_minutes} min</span>
                  <span className="ww-badge-slate"><Users className="h-3.5 w-3.5" /> {s.crew_size}-person crew</span>
                  <span className="ww-badge-gold">Starting ${s.base_price?.toFixed?.(0)}</span>
                </div>
                <div className="mt-8 flex gap-3">
                  <Link to="/book" className="ww-btn-primary">Book this service</Link>
                  <Link to="/quote" className="ww-btn-outline">Free estimate</Link>
                </div>
              </div>
              <div className="lg:col-span-6">
                <div className="rounded-md overflow-hidden ww-card-lg">
                  <img src={serviceImg(s.slug)} alt={`Wonder Washers ${s.name.toLowerCase()} service in Oregon`} className="w-full h-72 object-cover" loading="lazy" />
                </div>
              </div>
            </div>
          );
        })}
      </section>
    </>
  );
}
